import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { MutationPlanDialog, MutationPlanDialogData } from "./MutationPlanDialog";
import { MutationStepData } from "./MutationStepRow";
import {
  MUTATION_PLAN_QUERY,
  APPROVE_MUTATION_PLAN,
  CANCEL_MUTATION_PLAN,
  UPDATE_MUTATION_STEP_INPUT,
  RETRY_FAILED_STEPS,
} from "./graphql";
import { useRangerChatStore } from "../RangerChat/chatStore";
import { refetchTemplateFields } from "../Template/refetchTemplateFields";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

interface MutationPlanCardConnectedProps {
  planId: number;
}

const STATUS_CHIP: Record<string, { label: string; color: "default" | "primary" | "success" | "error" | "warning" }> = {
  STAGED: { label: "Awaiting Review", color: "primary" },
  EXECUTING: { label: "Executing", color: "warning" },
  COMPLETED: { label: "Completed", color: "success" },
  FAILED: { label: "Failed", color: "error" },
};

export const MutationPlanCardConnected: React.FC<MutationPlanCardConnectedProps> = ({ planId }) => {
  const [cancelled, setCancelled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dismissPlan = useRangerChatStore(s => s.dismissPlan);
  const undismissPlan = useRangerChatStore(s => s.undismissPlan);
  const setAutoSendMessage = useRangerChatStore(s => s.setAutoSendMessage);

  const { data, loading, error, refetch, stopPolling } = useQuery(MUTATION_PLAN_QUERY, {
    variables: { id: planId },
    pollInterval: cancelled ? 0 : 5000,
    skip: cancelled,
  });

  // COMPLETED/FAILED plans never change again, so polling them is pure waste — and
  // the query resolves liveSnapshot + valueLabels for every step, so a long roster
  // plan left open in a tab kept re-running those reads every 5s forever.
  const planStatus = data?.mutationPlan?.status;
  useEffect(() => {
    if (planStatus === "COMPLETED" || planStatus === "FAILED") stopPolling();
  }, [planStatus, stopPolling]);

  useEffect(() => {
    const status = data?.mutationPlan?.status;
    if (status === "STAGED") {
      undismissPlan(planId);
    } else if (status) {
      dismissPlan(planId);
    }
  }, [data?.mutationPlan?.status, planId, dismissPlan, undismissPlan]);

  // The plan executes server-side, so this browser's cache never sees the field
  // values it wrote. Pull them so an open Diligence form reflects an approved
  // change immediately instead of on its next 60s poll.
  const onPlanExecuted = useCallback(() => {
    refetch();
    refetchTemplateFields();
  }, [refetch]);

  const [approvePlan, { loading: approving }] = useMutation(APPROVE_MUTATION_PLAN, {
    onCompleted: onPlanExecuted,
  });
  const [cancelPlan, { loading: cancelling }] = useMutation(CANCEL_MUTATION_PLAN, {
    onCompleted: () => {
      setCancelled(true);
      setDialogOpen(false);
      dismissPlan(planId);
    },
  });
  const [retryPlan, { loading: retrying }] = useMutation(RETRY_FAILED_STEPS, {
    onCompleted: onPlanExecuted,
  });
  const [updateStepInput] = useMutation(UPDATE_MUTATION_STEP_INPUT);

  const handleApprove = useCallback(
    (id: number) => approvePlan({ variables: { planId: id } }),
    [approvePlan]
  );

  const handleCancel = useCallback(
    (id: number) => cancelPlan({ variables: { planId: id } }),
    [cancelPlan]
  );

  const handleRetry = useCallback(
    (id: number) => retryPlan({ variables: { planId: id } }),
    [retryPlan]
  );

  const handleStepInputChange = useCallback(
    (stepId: number, input: Record<string, any>) => {
      updateStepInput({ variables: { stepId, input } });
    },
    [updateStepInput]
  );

  const handleAskAgentToFix = useCallback(
    (_id: number, _errorMessage: string) => {
      const plan = data?.mutationPlan;
      const title = plan?.title ?? `#${_id}`;
      const failedSteps = (plan?.steps ?? [])
        .filter((s: any) => s.status === "FAILED")
        .map((s: any) => `Step "${s.mutationName}": ${s.errorMessage ?? "unknown error"}`);
      const errorDetail = failedSteps.length > 0
        ? failedSteps.join("; ")
        : _errorMessage;
      setAutoSendMessage(
        `Mutation plan "${title}" failed. Failed step(s): ${errorDetail}. ` +
        `Please diagnose the issue and stage a corrected plan.`
      );
    },
    [setAutoSendMessage, data?.mutationPlan]
  );

  if (cancelled) {
    return (
      <Paper elevation={1} sx={{ maxWidth: 500, my: 1, px: 2, py: 1.5, opacity: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CancelIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">
            Plan rejected
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (loading && !data) {
    return (
      <Paper elevation={1} sx={{ maxWidth: 500, my: 1, px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">Loading plan...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ maxWidth: 500, my: 1, px: 2, py: 1.5, opacity: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">
            Failed to load plan
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!data?.mutationPlan) {
    return (
      <Paper elevation={1} sx={{ maxWidth: 500, my: 1, px: 2, py: 1.5, opacity: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CancelIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">
            Plan rejected
          </Typography>
        </Box>
      </Paper>
    );
  }

  const plan = data.mutationPlan;
  const statusInfo = STATUS_CHIP[plan.status] ?? { label: plan.status, color: "default" as const };
  const stepCount = plan.steps?.length ?? 0;

  const planData: MutationPlanDialogData = {
    id: plan.id,
    title: plan.title,
    status: plan.status,
    expiresAt: plan.expiresAt,
    createdBy: plan.createdBy,
    steps: (plan.steps ?? []).map((s: any): MutationStepData => ({
      id: s.id,
      sequence: s.sequence,
      mutationName: s.mutationName,
      description: s.description,
      input: s.input ?? {},
      status: s.status,
      result: s.result,
      previousState: s.previousState,
      errorMessage: s.errorMessage,
      fieldSchema: s.fieldSchema,
      lockedFields: s.lockedFields,
      liveSnapshot: s.liveSnapshot,
      valueLabels: s.valueLabels,
      resultLinks: s.resultLinks,
      filePreviewUrl: s.filePreviewUrl,
      dataSources: s.dataSources,
    })),
  };

  const StatusIcon = plan.status === "COMPLETED"
    ? CheckCircleIcon
    : plan.status === "FAILED"
      ? ErrorIcon
      : plan.status === "STAGED"
        ? PlayArrowIcon
        : null;

  return (
    <>
      <Paper
        elevation={1}
        data-cy="mutation-plan-card"
        data-plan-id={planId}
        data-plan-status={plan.status}
        sx={{
          maxWidth: 500,
          my: 1,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: 3 },
        }}
        onClick={() => setDialogOpen(true)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {StatusIcon && (
            <StatusIcon
              fontSize="small"
              color={plan.status === "COMPLETED" ? "success" : plan.status === "FAILED" ? "error" : "primary"}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {plan.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stepCount} step{stepCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <Chip label={statusInfo.label} color={statusInfo.color} size="small" variant="outlined" />
          {plan.status === "STAGED" && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              data-cy="mutation-plan-review-btn"
              onClick={e => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              sx={{ ml: 1, textTransform: "none", minWidth: "auto", px: 1.5 }}
            >
              Review
            </Button>
          )}
          {plan.status === "FAILED" && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              data-cy="mutation-plan-ask-agent-btn"
              startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
              onClick={e => {
                e.stopPropagation();
                const failedStep = plan.steps?.find((s: any) => s.status === "FAILED");
                handleAskAgentToFix(plan.id, failedStep?.errorMessage ?? "Unknown error");
              }}
              sx={{ ml: 1, textTransform: "none", minWidth: "auto", px: 1.5 }}
            >
              Ask Agent to Fix
            </Button>
          )}
        </Box>
      </Paper>

      <MutationPlanDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        plan={planData}
        onApprove={handleApprove}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onAskAgentToFix={handleAskAgentToFix}
        onStepInputChange={handleStepInputChange}
        loading={approving || cancelling || retrying}
      />
    </>
  );
};
